'use client'

import { useCallback } from 'react'

import { useUserContext } from '@/context'
import { useSessionRuntime } from '@/context/SessionRuntimeContext'
import {
  buildGridViewPreferenceId,
  type GridViewItem,
} from '@/services/gridView/gridView.types'
import {
  type GridViewDeleteInput,
  type GridViewInput,
  type GridViewPreferenceInput,
  useCreateGridView,
  useDeleteGridView,
  useUpdateGridView,
  useUpsertGridViewPreference,
} from '@/services/mutations/gridView'

import { resolveGridViewPermissions } from './gridView.rules'

const GRID_VIEW_PERMISSION_ERROR =
  "Vous n'avez pas l'autorisation de modifier cette vue de grille."

type OrganizationScope = ReturnType<typeof useUserContext>['organizationScope']

const getActiveOrganizationId = (organizationScope: OrganizationScope) =>
  organizationScope.scopeKind === 'organization'
    ? organizationScope.activeOrganizationId
    : undefined

const throwPermissionError = (): never => {
  throw new Error(GRID_VIEW_PERMISSION_ERROR)
}

const hasResolvedViewScope = ({
  activeOrganizationId,
  userId,
  scopeKey,
}: {
  activeOrganizationId?: string
  userId?: string
  scopeKey?: string | null
}) => Boolean(activeOrganizationId && userId && scopeKey)

export const useAuthorizedCreateGridView = () => {
  const mutation = useCreateGridView()
  const { user, isGuest, isAdmin, organizationScope } = useUserContext()
  const { isImpersonating } = useSessionRuntime()

  const assertCanCreate = useCallback(
    (input: GridViewInput) => {
      const activeOrganizationId = getActiveOrganizationId(organizationScope)
      const userId = user?.id
      const permissions = resolveGridViewPermissions({
        activeOrganizationId,
        scopeKind: organizationScope.scopeKind,
        userId,
        isGuest,
        isAdmin,
      })

      if (
        isImpersonating ||
        !hasResolvedViewScope({
          activeOrganizationId,
          userId,
          scopeKey: input.scopeKey,
        }) ||
        input.organizationID !== activeOrganizationId ||
        input.createdByUserID !== userId ||
        input.updatedByUserID !== userId ||
        !permissions.canCreateSharedView ||
        (input.isDefault && !permissions.canSetDefaultSharedView)
      ) {
        throwPermissionError()
      }
    },
    [isAdmin, isGuest, isImpersonating, organizationScope, user?.id],
  )

  const mutate = (
    input: GridViewInput,
    options?: Parameters<typeof mutation.mutate>[1],
  ) => {
    assertCanCreate(input)
    return mutation.mutate(input, options)
  }

  const mutateAsync = useCallback(
    async (input: GridViewInput) => {
      assertCanCreate(input)
      return mutation.mutateAsync(input)
    },
    [assertCanCreate, mutation],
  )

  return { ...mutation, mutate, mutateAsync }
}

export const useAuthorizedUpdateGridView = () => {
  const mutation = useUpdateGridView()
  const { user, isGuest, isAdmin, organizationScope } = useUserContext()
  const { isImpersonating } = useSessionRuntime()

  const assertCanUpdate = useCallback(
    ({
      input,
      currentView,
    }: {
      input: GridViewInput
      currentView: GridViewItem
    }) => {
      const activeOrganizationId = getActiveOrganizationId(organizationScope)
      const userId = user?.id
      const permissions = resolveGridViewPermissions({
        activeOrganizationId,
        scopeKind: organizationScope.scopeKind,
        userId,
        isGuest,
        isAdmin,
        createdByUserID: currentView.createdByUserID,
      })
      const changesDefault =
        typeof input.isDefault === 'boolean' &&
        input.isDefault !== Boolean(currentView.isDefault)
      const changesImmutableScope =
        (input.organizationID != null &&
          input.organizationID !== currentView.organizationID) ||
        (input.scopeKey != null && input.scopeKey !== currentView.scopeKey) ||
        (input.createdByUserID != null &&
          input.createdByUserID !== currentView.createdByUserID)
      const hasInvalidUpdater =
        input.updatedByUserID != null && input.updatedByUserID !== userId

      if (
        isImpersonating ||
        !hasResolvedViewScope({
          activeOrganizationId,
          userId,
          scopeKey: input.scopeKey ?? currentView.scopeKey,
        }) ||
        currentView.organizationID !== activeOrganizationId ||
        input.id !== currentView.id ||
        changesImmutableScope ||
        hasInvalidUpdater ||
        !permissions.canUpdateSharedView ||
        (changesDefault && !permissions.canSetDefaultSharedView)
      ) {
        throwPermissionError()
      }
    },
    [isAdmin, isGuest, isImpersonating, organizationScope, user?.id],
  )

  const mutate = (
    variables: { input: GridViewInput; currentView: GridViewItem },
    options?: Parameters<typeof mutation.mutate>[1],
  ) => {
    assertCanUpdate(variables)
    return mutation.mutate(variables.input, options)
  }

  const mutateAsync = useCallback(
    async (variables: { input: GridViewInput; currentView: GridViewItem }) => {
      assertCanUpdate(variables)
      const { input } = variables
      return mutation.mutateAsync(input)
    },
    [assertCanUpdate, mutation],
  )

  return { ...mutation, mutate, mutateAsync }
}

export const useAuthorizedDeleteGridView = () => {
  const mutation = useDeleteGridView()
  const { user, isGuest, isAdmin, organizationScope } = useUserContext()
  const { isImpersonating } = useSessionRuntime()

  const assertCanDelete = useCallback(
    ({
      input,
      currentView,
    }: {
      input: GridViewDeleteInput
      currentView: GridViewItem
    }) => {
      const activeOrganizationId = getActiveOrganizationId(organizationScope)
      const userId = user?.id
      const permissions = resolveGridViewPermissions({
        activeOrganizationId,
        scopeKind: organizationScope.scopeKind,
        userId,
        isGuest,
        isAdmin,
        createdByUserID: currentView.createdByUserID,
      })

      if (
        isImpersonating ||
        !hasResolvedViewScope({
          activeOrganizationId,
          userId,
          scopeKey: input.scopeKey,
        }) ||
        currentView.organizationID !== activeOrganizationId ||
        input.id !== currentView.id ||
        input.organizationID !== currentView.organizationID ||
        input.scopeKey !== currentView.scopeKey ||
        input.updatedByUserID !== userId ||
        !permissions.canDeleteSharedView
      ) {
        throwPermissionError()
      }
    },
    [isAdmin, isGuest, isImpersonating, organizationScope, user?.id],
  )

  const mutate = (
    variables: {
      input: GridViewDeleteInput
      currentView: GridViewItem
    },
    options?: Parameters<typeof mutation.mutate>[1],
  ) => {
    assertCanDelete(variables)
    return mutation.mutate(variables.input, options)
  }

  const mutateAsync = useCallback(
    async (variables: {
      input: GridViewDeleteInput
      currentView: GridViewItem
    }) => {
      assertCanDelete(variables)
      const { input } = variables
      return mutation.mutateAsync(input)
    },
    [assertCanDelete, mutation],
  )

  return { ...mutation, mutate, mutateAsync }
}

export const useAuthorizedUpsertGridViewPreference = () => {
  const mutation = useUpsertGridViewPreference()
  const { user, isGuest, isAdmin, organizationScope } = useUserContext()
  const { isImpersonating } = useSessionRuntime()

  const assertCanUpsertPreference = useCallback(
    ({
      input,
    }: {
      upsertType: 'create' | 'update'
      input: GridViewPreferenceInput
    }) => {
      const activeOrganizationId = getActiveOrganizationId(organizationScope)
      const userId = user?.id
      const expectedId =
        activeOrganizationId && userId && input.scopeKey
          ? buildGridViewPreferenceId({
              userID: userId,
              organizationID: activeOrganizationId,
              scopeKey: input.scopeKey,
            })
          : null
      const permissions = resolveGridViewPermissions({
        activeOrganizationId,
        scopeKind: organizationScope.scopeKind,
        userId,
        isGuest,
        isAdmin,
      })

      if (
        isImpersonating ||
        !hasResolvedViewScope({
          activeOrganizationId,
          userId,
          scopeKey: input.scopeKey,
        }) ||
        input.id !== expectedId ||
        input.organizationID !== activeOrganizationId ||
        input.userID !== userId ||
        !permissions.canReadSharedViews
      ) {
        throwPermissionError()
      }
    },
    [isAdmin, isGuest, isImpersonating, organizationScope, user?.id],
  )

  const mutate = (
    variables: {
      upsertType: 'create' | 'update'
      input: GridViewPreferenceInput
    },
    options?: Parameters<typeof mutation.mutate>[1],
  ) => {
    assertCanUpsertPreference(variables)
    return mutation.mutate(variables, options)
  }

  const mutateAsync = useCallback(
    async (variables: {
      upsertType: 'create' | 'update'
      input: GridViewPreferenceInput
    }) => {
      assertCanUpsertPreference(variables)
      return mutation.mutateAsync(variables)
    },
    [assertCanUpsertPreference, mutation],
  )

  return { ...mutation, mutate, mutateAsync }
}
